/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(() => {
  const initialisePopup = () => {
    const popup = document.querySelector('[data-section-type="boyhood-newsletter-popup"]');
    if (!popup || popup.dataset.bhReady) return;
    popup.dataset.bhReady = 'true';
    const settings = JSON.parse(popup.dataset.sectionSettings || '{}');
    const key = 'boyhood-newsletter-seen';
    let dismissed = false;
    let engaged = false;
    let elapsed = false;
    let opened = false;
    try { dismissed = settings.showOnlyOnce && sessionStorage.getItem(key) === 'true'; } catch (_) {}
    const privacyResolved = () => {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.shouldShowBanner !== 'function') return false;
      const consent = privacy.currentVisitorConsent();
      return !privacy.shouldShowBanner() || [consent.analytics, consent.marketing, consent.preferences].some(value => value === 'yes' || value === 'no');
    };
    const showIfReady = () => {
      if (dismissed || opened || !engaged || !elapsed || !privacyResolved()) return;
      popup.removeAttribute('inert');
      popup.setAttribute('aria-hidden', 'false');
      opened = true;
      if (settings.showOnlyOnce) {
        try { sessionStorage.setItem(key, 'true'); } catch (_) {}
      }
    };
    const close = () => {
      popup.setAttribute('aria-hidden', 'true');
      popup.setAttribute('inert', '');
      dismissed = true;
    };
    popup.querySelector('[data-action="close-popup"]')?.addEventListener('click', close);
    popup.addEventListener('keydown', event => { if (event.key === 'Escape') close(); });
    window.addEventListener('scroll', () => {
      if (window.scrollY > 200) { engaged = true; showIfReady(); }
    }, { passive: true });
    document.addEventListener('click', event => {
      if (event.target.closest('main, header, .Header') && !popup.contains(event.target)) {
        engaged = true;
        showIfReady();
      }
    });
    document.addEventListener('visitorConsentCollected', () => setTimeout(showIfReady, 800));
    setTimeout(() => { elapsed = true; showIfReady(); }, Math.max(0, Number(settings.apparitionDelay) || 20) * 1000);
    if (window.Shopify?.customerPrivacy) showIfReady();
    else if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        if (!error) showIfReady();
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialisePopup);
  else initialisePopup();
  document.addEventListener('shopify:section:load', initialisePopup);
})();


if (!customElements.get('boyhood-hero-video')) {
  customElements.define('boyhood-hero-video', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button) {
        this.readyHandler = () => this.connectedCallback();
        document.addEventListener('DOMContentLoaded', this.readyHandler, { once: true });
        return;
      }
      this.initialised = true;
      this.cleanup = [];
      this.sourceIndex = 0;
      this.generation = 0;
      this.userPaused = false;
      this.inView = this.getBoundingClientRect().top < window.innerHeight;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoPlay = !this.motionPreference.matches && !(navigator.connection && navigator.connection.saveData);
      const mobile = window.matchMedia('(max-width: 640px)').matches;
      const original = mobile ? this.dataset.mobileSrc : this.dataset.desktopSrc;
      const hls = mobile ? this.dataset.mobileHls : this.dataset.desktopHls;
      const fallback = mobile ? this.dataset.mobileFallback : this.dataset.desktopFallback;
      const nativeHls = this.video.canPlayType('application/vnd.apple.mpegurl');
      this.sources = [...new Set((nativeHls ? [hls, fallback, original] : [original, fallback]).filter(Boolean))];
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.listen(this.video, 'timeupdate', () => {
        if (this.video.readyState >= 2 && this.video.currentTime >= 0.9) {
          this.dataset.hasFrame = 'true';
          clearTimeout(this.startTimer);
          this.startTimer = null;
        }
        this.updateButton();
      });
      this.listen(this.video, 'playing', () => this.updateButton());
      this.listen(this.video, 'pause', () => this.updateButton());
      this.listen(this.video, 'error', () => this.tryFallback());
      this.listen(this.button, 'click', () => {
        if (this.dataset.playing === 'true') {
          this.userPaused = true;
          this.suspend();
        } else {
          this.userPaused = false;
          this.autoPlay = true;
          this.inView = true;
          if (this.video.error) this.setSource();
          this.playVideo();
        }
      });
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.suspend();
        else if (this.autoPlay && this.inView && !this.userPaused) this.playVideo();
      });
      const motionChange = event => {
        if (event.matches) {
          this.autoPlay = false;
          this.suspend();
        }
      };
      if (this.motionPreference.addEventListener) this.listen(this.motionPreference, 'change', motionChange);
      else {
        this.motionPreference.addListener(motionChange);
        this.cleanup.push(() => this.motionPreference.removeListener(motionChange));
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          if (!this.inView) this.suspend();
          else if (this.autoPlay && !this.userPaused && !document.hidden) this.playVideo();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      }
      this.button.hidden = false;
      this.updateButton();
      if (this.autoPlay && this.inView && !document.hidden) this.playVideo();
    }
    listen(target, type, handler) {
      target.addEventListener(type, handler);
      this.cleanup.push(() => target.removeEventListener(type, handler));
    }
    setSource() {
      this.generation += 1;
      this.pending = false;
      this.dataset.hasFrame = 'false';
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.preload = 'auto';
      this.video.src = this.sources[this.sourceIndex];
      this.video.load();
    }
    playVideo() {
      if (this.pending || document.hidden || this.userPaused || !this.inView) return;
      if (!this.video.getAttribute('src')) this.setSource();
      this.video.muted = true;
      this.video.autoplay = this.autoPlay;
      const generation = this.generation;
      this.pending = true;
      if (this.dataset.hasFrame !== 'true' && !this.startTimer) {
        this.startTimer = setTimeout(() => {
          this.startTimer = null;
          if (this.dataset.hasFrame !== 'true' && !this.userPaused && this.inView && !document.hidden) this.tryFallback();
        }, 8000);
      }
      const attempt = this.video.play();
      if (attempt && attempt.then) attempt.then(() => {
        if (generation !== this.generation) return;
        this.pending = false;
        this.updateButton();
      }).catch(error => {
        if (generation !== this.generation) return;
        this.pending = false;
        if (error.name === 'NotAllowedError') {
          this.suspend();
          this.updateButton();
        } else if (error.name !== 'AbortError') this.tryFallback();
      });
      else this.pending = false;
    }
    tryFallback() {
      clearTimeout(this.startTimer);
      this.startTimer = null;
      if (this.userPaused || !this.inView || document.hidden) return;
      if (this.sourceIndex + 1 < this.sources.length) {
        this.sourceIndex += 1;
        this.setSource();
        this.playVideo();
      } else {
        this.suspend();
        this.dataset.hasFrame = 'false';
        this.updateButton();
      }
    }
    suspend() {
      clearTimeout(this.startTimer);
      this.startTimer = null;
      this.video.autoplay = false;
      this.video.pause();
    }
    updateButton() {
      const playing = !this.video.paused && this.video.readyState >= 2 && this.dataset.hasFrame === 'true';
      this.dataset.playing = String(playing);
      this.button.setAttribute('aria-label', playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel);
    }
    disconnectedCallback() {
      if (this.video) this.suspend();
      if (this.cleanup) this.cleanup.forEach(remove => remove());
      if (this.observer) this.observer.disconnect();
      if (this.readyHandler) document.removeEventListener('DOMContentLoaded', this.readyHandler);
      this.generation = (this.generation || 0) + 1;
      this.initialised = false;
    }
  });
}
